<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import WizardOverlayContainer from '@/components/WizardOverlayContainer.vue'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import StrategyFilterMenu from './StrategyFilterMenu.vue'
import { buildFilters, EMPTY_SELECTION, selectionFromQuery, selectionToQuery } from './strategy_filters'
import { useStrategyTags } from './useStrategyTags'
import type { StrategyFilter, StrategyFilterSelection } from './strategy_types'

const OTHER_SUBJECT = 'Other'
const SUBJECT_PROMPT = '(subject)'
const BARRIER_PROMPT_FOR_OTHER = '(select barrier)'

const PROMPTS: Record<string, { before: string; after?: string }> = {
  subject: { before: 'I need strategies for' },
  barrier: { before: 'to address a barrier related to' },
  grades: { before: 'for students in grades' },
  teachingType: { before: 'to', after: 'a skill/concept' },
  setting: { before: 'in a', after: 'instructional setting.' }
}

const MORE_OPTION_FIELDS = ['teachingType', 'setting']
// The prompts are a 30px italic sentence, but the controls inside it stay a normal
// upright control, as they are in Vue 2 — sizing them to the prompt made each one
// 74px tall, which broke the sentence onto one line per control and pushed the
// triggers so far down the page that their menus had to flip upwards.
const PROMPT_TRIGGER_CLASS = 'text-text-lg leading-normal not-italic'

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()
const { tags, loadingOptions, optionsError } = useStrategyTags()

const selection = computed(() => selectionFromQuery(route.query))
const subjectIsOther = computed(() => selection.value.subject === OTHER_SUBJECT)

const wizardFocus = ref(Boolean(selection.value.subject))
const showMoreFilters = ref(false)

const promptRows = computed(() =>
  buildFilters(selection.value, tags.value)
    .filter(isAsked)
    .map((filter) => {
      const prompt = PROMPTS[filter.field]
      const shownValue = filter.valueLabel || promptFor(filter)
      return {
        ...filter,
        emptyText: promptFor(filter),
        clearable: isClearable(filter),
        ...prompt,
        // The trigger's aria-label replaces its visible text, so the name has to carry
        // that text too (WCAG 2.5.3) or the chosen value is never announced.
        triggerLabel: [prompt.before, shownValue, prompt.after].filter(Boolean).join(' ')
      }
    })
)

const subjectRow = computed(() => promptRows.value.find((row) => row.field === 'subject'))
const barrierAndGradesRows = computed(() =>
  promptRows.value.filter((row) => row.field !== 'subject' && !MORE_OPTION_FIELDS.includes(row.field))
)
const moreOptionRows = computed(() => promptRows.value.filter((row) => MORE_OPTION_FIELDS.includes(row.field)))

const submitDisabled = computed(() => subjectIsOther.value && !selection.value.barrier)

function isAsked(filter: StrategyFilter) {
  if (filter.field === 'subject') {
    return true
  }
  if (!selection.value.subject) {
    return false
  }
  return filter.field !== 'barrier' || filter.options.length > 0
}

function promptFor(filter: StrategyFilter) {
  if (filter.field === 'subject') {
    return SUBJECT_PROMPT
  }
  if (filter.field === 'barrier' && subjectIsOther.value) {
    return BARRIER_PROMPT_FOR_OTHER
  }
  return filter.emptyText
}

function isClearable(filter: StrategyFilter) {
  if (filter.field === 'subject') {
    return false
  }
  return !(filter.field === 'barrier' && subjectIsOther.value)
}

function filtersEventData() {
  return {
    subject: selection.value.subject || undefined,
    barrier: selection.value.barrier || undefined,
    grades: selection.value.grades || undefined,
    setting: selection.value.setting || undefined,
    teachingType: selection.value.teachingType || undefined,
    query: selection.value.query || undefined
  }
}

function updateFilter(field: keyof StrategyFilterSelection, value: string | string[] | null) {
  const nextSelection = { ...selection.value, [field]: value } as StrategyFilterSelection
  if (field === 'subject') {
    nextSelection.barrier = ''
  }
  router.push({ name: 'StrategyWizard', query: selectionToQuery(nextSelection, route.query) })
}

function unfocus() {
  wizardFocus.value = false
  showMoreFilters.value = false
}

function startOver() {
  showMoreFilters.value = false
  // starting over clears the selection but must not clear the opt-in
  router.replace({ name: 'StrategyWizard', query: selectionToQuery(EMPTY_SELECTION, route.query) })
}

function findStrategies() {
  // route.query carries the ?beta=true opt-in across the handoff, so the receiving
  // page is the Vue 3 one the reviewer opted into rather than its Vue 2 version
  router.push({ name: 'BrowseStrategiesPage', query: selectionToQuery(selection.value, route.query) })
  analytics.sendEvent(EVENT_NAMES.STRATEGY_WIZARD.SUBMITTED, filtersEventData())
}

async function toggleMoreFilters() {
  showMoreFilters.value = !showMoreFilters.value
  if (!showMoreFilters.value) {
    return
  }
  await nextTick()
  const firstRevealed = document.querySelector<HTMLElement>(`[data-testid="filter-${MORE_OPTION_FIELDS[0]}"]`)
  firstRevealed?.focus()
}

watch(
  () => selection.value.subject,
  (subject) => updateFeedbackContext({ subject }),
  { immediate: true }
)

analytics.sendEvent(EVENT_NAMES.STRATEGY_WIZARD.VIEWED_PAGE, {
  ...filtersEventData(),
  ref: route.query.ref
})
</script>

<template>
  <div class="container min-h-screen">
    <header class="mb-14 text-center">
      <h1 class="header-xl mb-4 mt-8 font-regular text-gray-900">Strategy Wizard</h1>
      <p class="mx-auto text-text-xl text-gray-800">
        Find specific strategies for a particular subject and area of need.
      </p>
    </header>

    <div>
      <WizardOverlayContainer :activated="wizardFocus" theme="green" label="Strategy Wizard" @unfocus="unfocus">
        <button
          v-if="wizardFocus"
          data-testid="close-wizard"
          type="button"
          aria-label="Close the Strategy Wizard"
          class="absolute right-5 top-4 text-gray-900 opacity-50 hover:opacity-70 focus-visible:outline-offset-2"
          @click="unfocus"
        >
          <span class="material-icons-round text-display-sm" aria-hidden="true">close</span>
        </button>

        <div v-if="subjectRow" class="text-display-md italic leading-[3.75rem] text-gray-900">
          {{ subjectRow.before }}
          <StrategyFilterMenu
            :filter="subjectRow"
            :trigger-label="subjectRow.triggerLabel"
            :clearable="subjectRow.clearable"
            :trigger-class="PROMPT_TRIGGER_CLASS"
            container-portal="[data-testid='wizard-overlay']"
            @open="wizardFocus = true"
            @select="updateFilter('subject', $event)"
          />
        </div>

        <div v-if="selection.subject">
          <div
            v-for="row in barrierAndGradesRows"
            :key="row.field"
            class="text-display-md italic leading-[3.75rem] text-gray-900"
          >
            {{ row.before }}
            <StrategyFilterMenu
              :filter="row"
              :trigger-label="row.triggerLabel"
              :clearable="row.clearable"
              :trigger-class="PROMPT_TRIGGER_CLASS"
            container-portal="[data-testid='wizard-overlay']"
              @open="wizardFocus = true"
              @select="updateFilter(row.field, $event)"
            />
          </div>

          <div v-if="showMoreFilters">
            <div
              v-for="row in moreOptionRows"
              :key="row.field"
              class="text-display-md italic leading-[3.75rem] text-gray-900"
            >
              {{ row.before }}
              <StrategyFilterMenu
                :filter="row"
                :trigger-label="row.triggerLabel"
                :clearable="row.clearable"
                :trigger-class="PROMPT_TRIGGER_CLASS"
            container-portal="[data-testid='wizard-overlay']"
                @open="wizardFocus = true"
                @select="updateFilter(row.field, $event)"
              />
              {{ row.after }}
            </div>
          </div>

          <button
            type="button"
            :aria-expanded="showMoreFilters"
            aria-live="polite"
            class="text-blue-400 underline-offset-4 hover:underline focus-visible:outline-offset-2"
            @click="toggleMoreFilters"
          >
            {{ showMoreFilters ? 'Fewer options' : 'More options' }}
          </button>

          <div class="mt-6 text-center">
            <button
              type="button"
              :disabled="submitDisabled"
              class="rounded-xs bg-blue-400 px-6 py-4 text-text-2xl text-white disabled:opacity-50 focus-visible:outline-offset-2"
              @click="findStrategies"
            >
              Find Strategies
            </button>
            <button
              data-testid="start-over"
              type="button"
              class="ml-4 px-6 py-4 text-text-2xl text-blue-400 underline-offset-4 hover:underline focus-visible:outline-offset-2"
              @click="startOver"
            >
              Start Over
            </button>
          </div>
        </div>

        <p v-if="loadingOptions" role="status" class="mt-6 text-text-lg text-gray-800">Loading options...</p>
        <p v-else-if="optionsError" role="alert" class="mt-6 text-text-lg text-gray-900">{{ optionsError }}</p>

        <div v-if="!wizardFocus" class="text-right">
          <router-link
            :to="{ name: 'StrategyIndex' }"
            class="whitespace-nowrap text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4"
          >
            View strategy index
          </router-link>
        </div>
      </WizardOverlayContainer>
    </div>

  </div>
</template>
